import React, { useState } from "react";
import { Box, Chip, Typography } from "@mui/material";
import CustomTextField from "./CustomTextFeild";

const isValidEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email.trim());
};

interface MultiEmailInputProps {
  value: string[];
  onChange: (emails: string[]) => void;
  labelText?: string;
  helperText?: string;
  error?: boolean;
  required?: boolean;
  maxLength?: number;
}

const MultiEmailInput: React.FC<MultiEmailInputProps> = ({
  value,
  onChange,
  labelText,
  helperText,
  required = false,
  error = false,
  maxLength = 50,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [localError, setLocalError] = useState<string>("");

  const addEmail = (raw: string) => {
    const candidate = (raw || "").trim().replace(/,$/, "");
    if (!candidate) {
      setInputValue("");
      setLocalError("");
      return;
    }

    if (!isValidEmail(candidate)) {
      setLocalError("Invalid email");
      setInputValue(candidate);
      return;
    }

    if (value.includes(candidate)) {
      setLocalError("Email already added");
      setInputValue(candidate);
      return;
    }

    onChange([...value, candidate]);
    setInputValue("");
    setLocalError("");
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter" || event.key === "," || event.key === " ") {
      event.preventDefault();
      addEmail(inputValue);
    }
  };

  const handleBlur = () => {
    addEmail(inputValue);
  };

  const handleDelete = (emailToDelete: string) => {
    onChange(value.filter((email) => email !== emailToDelete));
    setLocalError("");
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value;
    if (localError) setLocalError("");
    setInputValue(v);
  }; 
  
  const showError = Boolean(localError || (error && helperText));
  const effectiveHelperText = localError || (error ? helperText || "" : "");

  return (
    <Box>
      {value && value.length > 0 && (
        <>
          <Typography variant="subtitle2" sx={{ mb: 0.5, fontWeight: 600 }}>
            Selected recipients for sending the email:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1 }}>
            {value.map((email) => (
              <Chip
                key={email}
                label={email}
                onDelete={() => handleDelete(email)}
                color={isValidEmail(email) ? "default" : "error"}
                size="small"
              />
            ))}
          </Box>
        </>
      )}

      <CustomTextField
        value={inputValue}
        required={required}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        labelText={labelText}
        error={showError}
        helperText={effectiveHelperText}
        inputProps={{ maxLength }}
      />
    </Box>
  );
};

export default MultiEmailInput;
